<script setup lang="ts">
import type { ChartOptions } from 'chart.js'
import {
  Chart as ChartJS,
  Filler,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from 'chart.js'
import { Radar } from 'vue-chartjs'

const props = withDefaults(defineProps<{
  metrics: Record<string, number | null>
  labels?: Record<string, string | string[]>
  color?: string
  fillOpacity?: number
  hideLabels?: boolean
}>(), {
  color: '#254699',
  fillOpacity: 0,
  hideLabels: false,
})

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip)

const metricKeys = computed(() => Object.keys(props.labels ?? props.metrics))

const backgroundColor = computed(() =>
  `color-mix(in srgb, ${props.color} ${props.fillOpacity * 100}%, transparent)`,
)

const chartData = computed(() => ({
  labels: metricKeys.value.map(k => props.labels?.[k] ?? k),
  datasets: [
    {
      data: metricKeys.value.map(k => props.metrics[k] ?? 0),
      backgroundColor: backgroundColor.value,
      borderColor: props.color,
      borderWidth: 1.5,
      pointRadius: 0,
      pointHitRadius: 8,
    },
  ],
}))

const chartOptions = computed<ChartOptions<'radar'>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    r: {
      min: 0,
      max: 10,
      ticks: {
        display: false,
        stepSize: 2,
      },
      grid: {
        color: 'rgba(0, 0, 0, 0.15)',
      },
      angleLines: {
        color: 'rgba(0, 0, 0, 0.15)',
      },
      pointLabels: {
        display: !props.hideLabels,
        font: { family: 'Sora', size: 12, weight: 'normal', lineHeight: 1.4 },
        color: '#3F4D62',
      },
    },
  },
  plugins: {
    tooltip: {
      displayColors: false,
      callbacks: {
        label: (ctx: { raw: unknown }) => `${ctx.raw}`,
      },
    },
  },
}))
</script>

<template>
  <Radar :data="chartData" :options="chartOptions" />
</template>
